import { Add, Clear, Launch, Search } from "@mui/icons-material";
import { Box, Button, IconButton, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getProductDetails } from "../../slices/productcostseeds.slice";
import { RootState } from "../../store";
import {
  CompositeFilterDescriptor,
  DataResult,
  SortDescriptor,
  process,
} from "@progress/kendo-data-query";
import { AddEditProductPriceModal } from "./AddEditProductPriceModal";
import { Grid, GridColumn, GridPageChangeEvent } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import styled from "styled-components";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const StyledGrid = styled(Grid)`
  .k-column-title {
    min-width: 0;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    font-weight: bold; /* Make the font bold */
    font-family: "Roboto";
  }
  ,
  th {
    border-inline-start-width: 0px !important;
  }
  th > .k-cell-inner > .k-link {
    border-right: 2px solid var(--kendo-color-border);
    justify-content: center;
  }
  ,
  th > .k-cell-inner,
  .k-grid-header-wrap {
    background-color: white;
    height: 55px;
  }
  ,
  .k-table-row,
  .k-table-alt-row,
  .k-grid-table-wrap {
    background-color: #f3f4f6 !important;
  }
  ,
  .k-table-td {
    border-bottom: 1px solid var(--kendo-color-border);
    border-left: none;
    border-right: none;
    font-family: "Roboto";
    text-align: center;
  }
  .k-grid tr {
    background-color: #ffffff; /* White background for regular rows */
  }

  .k-grid tr.k-alt {
    background-color: #ffffff; /* Light grey background for alternate rows */
  }

  k-grid .k-table-row.k-table-alt-row {
    background-color: #f9f9f9;
  }
`;
export const Products = () => {
  const dispatch = useAppDispatch();
  const [productSearchQuery, setProductSearchQuery] = useState<string>('');
  const [filter] =
    useState<CompositeFilterDescriptor>(initialFilters);
  const [sort] = useState<SortDescriptor[]>([]);
  const exportRef = useRef<ExcelExport | null>(null);
  const { productsDetails } = useAppSelector(
    (state: RootState) => state.seedsdropdown
  );

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [page, setPage] = useState({ skip: 0, take: 20 });
  const handleProductSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProductSearchQuery(event.target.value);
  };

  const handleProductSearchKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      setProductSearchQuery((event.target as HTMLInputElement).value);
    }
  };

  let productsFilteredData = productsDetails.filter((item) =>
    item.name
      .toLowerCase()
      .includes(productSearchQuery && productSearchQuery.toLowerCase())
  );

  const onEditClick = (dataItem: any) => {
    setIsEdit(true);
    setSelectedProduct(dataItem);
    setOpen(true);
  };

  const onAddClick = () => {
    setIsEdit(false);
    setSelectedProduct(null);
    setOpen(true);
  };

  const handleClearSearch = () => {
    setProductSearchQuery("");
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const gridColumns = [
    {
      field: "name",
      title: "Product Name",
      width: 180,
    },
    {
      field: "priceUnit",
      title: "Price Unit",
      sortable: false,
      width: 280,
    },
    {
      field: "formattedPeriods",
      title: "Price Periods",
      sortable: false,
      width: 250,
    },
    {
      field: "linkedJdOpsProducts",
      title: "Linked JD Ops Products",
      sortable: false,
      width: 360,
    },
    {
      title: "Actions",
      width: "100px",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={onEditClick} />
        ),
      },
    },
  ];

  useEffect(() => {
    dispatch(getProductDetails({}));
  }, []);

  useEffect(() => {
    setPage((prev) => ({ ...prev, skip: 0 }));
  }, [productSearchQuery]);

  const handleClose = () => {
    dispatch(getProductDetails({}));
    setOpen(false);
  };

  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(productsFilteredData, {
        sort: sort,
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <TextField
          placeholder="Search Product"
          variant="outlined"
          size="small"
          value={productSearchQuery}
          onChange={handleProductSearchChange}
          onKeyPress={handleProductSearchKeyPress}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleClearSearch}
                style={productSearchQuery ? {} : { visibility: "hidden" }}
              >
                <Clear />
              </IconButton>
            ),
            startAdornment: <Search />,
          }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={onAddClick}>
          Add Product
        </Button>
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
        >
          Export
        </Button>
      </Box>
      <Box>
        <ExcelExport data={productsDetails} ref={exportRef}>
          <StyledGrid
            data={productsFilteredData}
            dataItemKey="id"
            filter={filter}
            sort={sort}
            scrollable="scrollable"
            autoProcessData={true}
            pageable={{ buttonCount: 5, pageSizes: [10, 20, 30], info: true }}
            total={productsFilteredData.length}
            skip={page.skip}
            take={page.take}
            onPageChange={handlePageChange}
          >
            {gridColumns.map((columnProps: any) => (
              <GridColumn
                key={columnProps.field || columnProps.title}
                {...columnProps}
                title={columnProps.title}
              />
            ))}
          </StyledGrid>
        </ExcelExport>
      </Box>
      {open && (
        <AddEditProductPriceModal
          isEdit={isEdit}
          open={open}
          handleClose={handleClose}
          product={selectedProduct}
        />
      )}
    </Box>
  );
};
